import { Divider, Grid, TextField, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { styles } from './customer-capture-styles';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblem';
import BaseButton from 'src/common/components/base-button';
import {
  handleInputChange,
  validateCustomerInputs,
} from '../business-logic/helper/customer-capture-helper';
import { CustomerDetailsFieldsObj, CustomerDetailsInfo } from '../interfaces';
import EmeraldDialog from '../../common/components/emerald-dialog/emerald-dialog';

const modalId = 'CustomerCapture';

export const showCustomerCaptureDialog = (props: CustomerCaptureProps): void => {
  pushEmeraldModal(<CustomerCapture {...props} />, undefined, modalId);
};

export const closeCustomerCaptureDialog = (): void => {
  popEmeraldModal(modalId);
};

export interface CustomerCaptureProps {
  customerDetails: CustomerDetailsFieldsObj;
  onSubmit: (inputFields: CustomerDetailsInfo) => void;
  onClose: () => void;
}

export const CustomerCapture = (props: CustomerCaptureProps) => {
  const { t } = useTranslation();
  const classes = styles();
  const { customerDetails } = props;
  const inputDictionary = {
    firstNameRequired: t('CustomerDetailsForReturnView_FirstName'),
    lastNameRequired: t('CustomerDetailsForReturnView_LastName'),
    addressRequired: t('CustomerDetailsForReturnView_Address'),
    cityRequired: t('CustomerDetailsForReturnView_City'),
    provinceRequired: t('CustomerDetailsForReturnView_Provence'),
    postalCodeRequired: t('CustomerDetailsForReturnView_PostalCode'),
  };

  /* this is for testing the UI quickly without 
  having to change the return policy every time */

  // const customerDetails = {
  //   firstNameRequired: true,
  //   lastNameRequired: true,
  //   addressRequired: true,
  //   cityRequired: true,
  //   provinceRequired: true,
  //   postalCodeRequired: false,
  // };

  const inputNames = Object.keys(customerDetails).reduce(
    (accumulator: string[], current: string): string[] => {
      if (customerDetails[current]) {
        return [...accumulator, current];
      } else {
        return accumulator;
      }
    },
    [],
  );

  const twoColumn = inputNames.length > 3;

  const fourFields = inputNames.length === 4;

  const [inputFields, setInputFields] = useState<CustomerDetailsInfo>({
    address: {
      required: !!inputNames.find(name => name === 'addressRequired'),
      value: '',
    },
    city: {
      required: !!inputNames.find(name => name === 'cityRequired'),
      value: '',
    },
    firstName: {
      required: !!inputNames.find(name => name === 'firstNameRequired'),
      value: '',
    },
    lastName: {
      required: !!inputNames.find(name => name === 'lastNameRequired'),
      value: '',
    },
    postalCode: {
      required: !!inputNames.find(name => name === 'postalCodeRequired'),
      value: '',
    },
    province: {
      required: !!inputNames.find(name => name === 'provinceRequired'),
      value: '',
    },
  });

  return (
    <EmeraldDialog
      classes={{
        paper: classes.dialogPaper,
      }}
      open={true}
      fullScreen
      data-testid='return-customer-details'
    >
      <Grid
        container
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        className={classes.titleBar}
      >
        <Grid item className={classes.titleBarTitleContainer} wrap={'nowrap'}>
          <Typography className={classes.titleBarTitle} noWrap={true}>
            {t('CustomerDetailsForReturnTitle')}
          </Typography>
        </Grid>
        <Grid item className={classes.icon}>
          <ReportProblemOutlinedIcon color='primary' />
        </Grid>
      </Grid>
      <Grid className={classes.mainContainer} wrap={'nowrap'}>
        <Typography className={classes.title} noWrap={true}>
          {t('CustomerDetailsForReturnTitle')}
        </Typography>
        <Typography className={classes.instructions} noWrap={true}>
          {t('HeaderControl_CustomerDetailsForReturn')}
        </Typography>
        <Grid
          className={classes.inputsContainer}
          container
          justifyContent='space-between'
          alignItems='center'
        >
          <Grid
            alignItems={twoColumn ? 'flex-start' : 'center'}
            justifyContent='center'
            container
            direction='column'
            item
            xs={twoColumn ? 5 : 12}
          >
            {/* if we have four fields, lay them out 2 by 2, otherwise get the first 3 */}
            {inputNames
              .filter((element, index) => (fourFields ? index <= 1 : index <= 2))
              .map((item, index) => (
                <div key={item} className={classes.inputContainer}>
                  <CustomerCaptureInput
                    autoFocus={index === 0}
                    name={inputDictionary[item]}
                    value={inputFields[item]}
                    onChange={handleInputChange(item, inputFields, setInputFields)}
                    testId={`return-customer-capture-${item}`}
                  />
                </div>
              ))}
          </Grid>
          {/* the divider only shows up for two column layout */}
          {twoColumn && (
            <Grid container className={classes.dividerContainer} item xs={2}>
              <Divider className={classes.divider} orientation='vertical' flexItem />
            </Grid>
          )}
          {/* if we have four fields, we only need to get the ones we didn't grab in the first grid (index 2 and 3), otherwise get the rest */}
          {twoColumn && (
            <Grid
              alignItems='flex-start'
              justifyContent='center'
              container
              direction='column'
              item
              xs={5}
            >
              {inputNames
                .filter((element, index) => (fourFields ? index > 1 : index > 2))
                .map(item => (
                  <div key={item} className={classes.inputContainer}>
                    <CustomerCaptureInput
                      name={inputDictionary[item]}
                      value={inputFields[item]}
                      onChange={handleInputChange(item, inputFields, setInputFields)}
                      testId={`return-customer-capture-${item}`}
                    />
                  </div>
                ))}
            </Grid>
          )}
        </Grid>
        <Grid justifyContent='flex-end' container>
          <BaseButton
            className={classes.cancelButton}
            buttonType='white'
            onClick={props.onClose}
            data-testid='return-customer-capture-cancel'
          >
            {t('Cancel_Button')}
          </BaseButton>
          <BaseButton
            data-testid='return-customer-capture-submit'
            disabled={validateCustomerInputs(inputFields)}
            onClick={() => props.onSubmit(inputFields)}
          >
            {t('Ok')}
          </BaseButton>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

interface CustomerCaptureInputProps {
  name: string;
  autoFocus?: boolean;
  value: string;
  onChange: (ev) => void;
  testId: string;
}

const CustomerCaptureInput = (props: CustomerCaptureInputProps) => {
  const { name, value, autoFocus = false, onChange, testId } = props;
  const classes = styles();
  return (
    <TextField
      inputProps={{
        className: 'global-keyboard',
        'data-testid': testId,
      }}
      InputProps={{
        classes: { underline: classes.textFieldUnderline },
      }}
      InputLabelProps={{
        classes: { focused: classes.inputLabel },
      }}
      classes={{ root: classes.fullWidthInput }}
      value={value}
      label={name}
      autoFocus={autoFocus}
      color='primary'
      onChange={onChange}
    />
  );
};
